import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
	PhoneOutgoing,
	Mail,
	X,
	FileText,
} from 'react-feather';
import { useSelector } from 'react-redux';
import ProgressBar from './ProgressBar';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
	fetchOrders,
	fetchOrderById,
	updateOrderStatus,
	updateOrdersNotes,
	updateInstallerId,
	updateOrdersPermit,
	createPermit,
} from '../slices/ordersSlice';
import { fetchUsers } from '../slices/userSlice';

import axios from 'axios';
import usePopup from '../hooks/popuphook';
import OrderNotesSection from './orderDetailsSections/OrderNotesSection';
import FileViewerModal from './modals/FileViewerModal';
import OrderSalesmanSection from './orderDetailsSections/OrderSalesmanSection';
import OrderClientInfoSection from './orderDetailsSections/OrderClientInfoSection';
import OrderPricingSummary from './orderDetailsSections/OrderPricingSummary';
import OrderInstallerSection from './orderDetailsSections/OrderInstallerSection';
import OrderPermitsSection from './orderDetailsSections/OrderPermitsSection';
import OrderPaymentHistorySection from './orderDetailsSections/OrderPaymentHistorySection';

import OrderStatusSection from './orderDetailsSections/OrderStatusSection';
import BncLogoSpinner from './BncLogoSpinner';
import { generateContractPDF } from '../utils/pdfGenerator';
import { sendContract as sendContractApi } from '../docusign/api';
import { buildContractSentNote } from '../utils/contractTracking';
// import { sendContractDirectly } from '../docusign/directSend';

export default function OrderDetails({ order, onClose }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const orderDetails = useSelector((state) => state.orders.order);
	const detailsStatus = useSelector((state) => state.orders.detailsStatus);
	const orderError = useSelector((state) => state.orders.error);
	// Use Redux detail (full data) when available, fall back to the row-level prop immediately.
	// This ensures the panel opens instantly while fetchOrderById loads in the background.
	const activeOrder = orderDetails || order;
	// True only when Redux has loaded the FULL order matching what was clicked.
	// While fetchOrderById is in-flight (or the stale order is a different id),
	// we show a spinner instead of blank partial data from the list-item prop.
	const orderLoaded = !!(orderDetails && order && orderDetails.id === order.id);
	// const orders = useSelector((state) => state.orders.items);
	const currentUser = useSelector((state) => state.auth.self);
	const [isSending, setIsSending] = useState(false);
	// const client = useSelector((state) => state.clients.clientById);
	const usersList = useSelector((state) => state.auth?.users);
	// update salesman and installer
	const [salesman, setSalesman] = useState(orderDetails?.salesman);
	const [installerId, setInstallerId] = useState(orderDetails?.installer);
	// update permit
	const [fileUrl, setFileUrl] = useState('');
	const [permitMsg, setPermitMsg] = useState('');

	const [permitProgress, setPermitProgress] = useState({
		started: false,
		pc: 0,
		msg: '',
	});

	console.log('OrderDetails order:', orderDetails);

	const [signerEmail, setSignerEmail] = useState('');
	const [signerName, setSignerName] = useState('');
	const [ccEmail, setCcEmail] = useState('');
	const [ccName, setCcName] = useState('');
	const [envelopeId, setEnvelopeId] = useState(null);
	const [contractPreview, setContractPreview] = useState(null);
	const [isPreparingContract, setIsPreparingContract] = useState(false);
	const [isContractViewerOpen, setIsContractViewerOpen] = useState(false);

	// Memoized to avoid infinite re-render loop: .filter() returns a new array
	// reference on every call, causing useSelector to think the value changed.
	const installers = useMemo(
		() => (usersList || []).filter((user) => user.role === 'INSTALLER'),
		[usersList]
	);

	// Fetch users list once per session (for installer assignment dropdown).
	// Gate on usersList being empty so we don't refetch on every panel open.
	// NOTE: fetchSelf is intentionally NOT dispatched here — App.js handles it.
	useEffect(() => {
		if (!usersList || usersList.length === 0) {
			dispatch(fetchUsers({ pageSize: 1000 }));
		}
	}, [dispatch, usersList]);

	useEffect(() => {
		if (orderDetails && orderDetails.client) {
			const { firstName, lastName } = orderDetails.client;
			console.log('First Name:', firstName);
			console.log('Last Name:', lastName);
			setSignerName(`${firstName} ${lastName}`);
			setSignerEmail(activeOrder?.client?.email);
			setCcName(orderDetails?.salesman?.fullName);
			setCcEmail(orderDetails?.salesman?.email);
		}
	}, [orderDetails]);

	useEffect(() => {
		setContractPreview(null);
		setIsContractViewerOpen(false);
	}, [orderDetails?.id]);

    useEffect(() => {
        if (orderDetails) {
            setSalesman(orderDetails.salesman);
            setInstallerId(orderDetails.installer);
        }
    }, [orderDetails]);

	const onSubmitFile = async (data) => {
		if (!orderId) return;
		// Check if newText is not empty
		const newNote = {
			userId: currentUser.id,
			createdAt: new Date().toISOString(),
			text: data.textNote,
			attachments: urlState.map((fileObj) => ({
				description: fileObj.name || 'Attachment', // Use preserved filename
				url: fileObj.url,
                type: fileObj.type
			})),
		};

		// Add the new note to the existing notes
		const newNotes = [...existingNotes, newNote];

		// dispatch the action to update the notes
		await dispatch(
			updateOrdersNotes({
				id: orderId,
				notes: newNotes,
			})
		);
		// Clear the input fields
		dispatch(fetchOrders(pageSize));
		setNewNoteText('');
		setUrlState([]);

		reset({
			textNote: '',
		});
		// Clear file inputs
		setFileInputs([0]);

		// Reset the form to clear the file input
		// Reset the form to clear the file input
		if (formRef.current) {
			formRef.current.reset();
		}

		dispatch(fetchOrderById(orderId));
	};

	// if current user and userList.id is the same return the user name userlist.name
	const getUserName = (userId) => {
		if (!userId) {
			return 'Unknown';
		}

		const user = usersList?.find(
			(user) => user?.id?.trim().toLowerCase() === userId.trim().toLowerCase()
		);

		return user ? user?.firstName + ' ' + user?.lastName : 'Unknown';
	};

	// Form to update order status

	const {
		register,
		handleSubmit,
		reset,
		setValue,
        watch,
		formState: { errors },
	} = useForm({
		defaultValues: {
			status: orderDetails?.status || 1,
		},
	});

	const pageSize = 100;

	const { isOpen, openPopup, closePopup } = usePopup();
	const [editFrom, setEditForm] = useState(false);
	const api = process.env.REACT_APP_API_URL;
	const formRef = useRef();

	// read the users and set the account manager value to the user id and label to the user name from

	//  add more fileds to upload file
	const [fileInputs, setFileInputs] = useState([0]);
	// sending files
	const existingNotes = activeOrder?.notes || [];
	const orderId = activeOrder?.id;
	// here is the file url
	const [urlState, setUrlState] = useState([]);
	const [newNoteText, setNewNoteText] = useState('');
	// Uploading progress bar
	const [Msg, setMsg] = useState(null);
	const [progress, setProgress] = useState({ started: false, pc: 0, msg: '' });

    // File Viewing
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

	// upload and remove inputs
	const addFileInput = () => {
		setFileInputs([...fileInputs, fileInputs.length]);
	};
	const removeFileInput = () => {
		if (fileInputs.length > 1) {
			const newFileInputs = fileInputs.slice(0, -1);
			setFileInputs(newFileInputs);
		}
	};

	// upload file
	const fileUploadHandler = async (file) => {
		const token = localStorage.getItem('token');

		// upload file by file and wait for the response to copy the url
		const formData = new FormData();
		if (file) {
			// Create a new File object with the same file data but a modified name
			const newFile = new File([file], file.name.replace(/\s/g, '_'), {
				type: file.type,
			});

			formData.append('file', newFile);

			setMsg('Uploading...');
			setProgress({ started: true, pc: 0, msg: 'Uploading...' });
		}

		try {
			const response = await axios.post(`${api}/files/upload`, formData, {
				onUploadProgress: (progressEvent) => {
					const pc = Math.round(
						(progressEvent.loaded / progressEvent.total) * 100
					);
					setProgress({ started: true, pc, msg: 'Uploading...' });
				},
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: `Bearer ${token}`,
				},
			});

			// Store object with url, name, and type
			setUrlState([
                ...urlState, 
                {
                    url: response.data.fileUrl,
                    name: file.name,
                    type: file.type
                }
            ]);
			setMsg('File uploaded successfully!');
			setProgress({
				started: false,
				pc: 0,
				msg: 'File uploaded successfully ready to send or add more files!',
			});
		} catch (error) {
			setMsg('Error uploading file!');
		}
	};

	const handleFileUpload = async (file) => {
		const token = localStorage.getItem('token');
		const formData = new FormData();
		const newFile = new File([file], file.name.replace(/\s/g, '_'), {
			type: file.type,
		});

		formData.append('file', newFile);

		try {
			const response = await axios.post(`${api}/files/upload`, formData, {
				onUploadProgress: (progressEvent) => {
					const pc = Math.round(
						(progressEvent.loaded / progressEvent.total) * 100
					);
					setPermitProgress({ started: true, pc, msg: 'Uploading...' });
				},
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: `Bearer ${token}`,
				},
			});

			console.log('response', response.data); // Log the entire response to check the structure
			if (response.data && response.data.fileUrl) {
				setFileUrl(response.data.fileUrl);
				console.log('fileUrl set to:', response.data.fileUrl); // Log the URL being set
				setPermitMsg('File uploaded successfully!');
				setPermitProgress({
					started: false,
					pc: 0,
					msg: 'File uploaded successfully, ready to send or add more files!',
				});
			} else {
				setPermitMsg('Error: No URL returned from upload.');
			}
		} catch (error) {
			console.error('Error uploading file:', error); // Log the error
			setPermitMsg('Error uploading file!');
		}
	};

	const handlePermitSubmit = async () => {
		if (!fileUrl) {
			setPermitMsg('Please upload a file first.');
			return;
		}

		const permitData = {
			orderId: orderDetails?.id,
			details: fileUrl,
			status: 1,
		};

		await dispatch(createPermit(permitData));
		await dispatch(fetchOrderById(orderDetails?.id));
	};

	const [sections, setSections] = useState({
		sale: false,
		client: false,
		structure: false,
		installer: false,
		notes: false,
		permits: false,
		invoice: false,
		recomendations: false,
		statussection: false,
	});

	const toggleSection = (section) => {
		setSections((prevSections) => ({
			...prevSections,
			[section]: !prevSections[section],
		}));
	};

	if (!order) return null;

	// Show spinner only if we have no data at all yet (neither from Redux nor from the prop)
	if (detailsStatus === 'loading' && !activeOrder) {
		return (
		<div className="order-details-panel">
			<aside className="details-panel flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
				<BncLogoSpinner size={80} />
			</aside>
		</div>
		);
	}

	const persistContractSentEvent = async (nextEnvelopeId) => {
		if (!orderId) {
			throw new Error('Order details are still loading.');
		}
		const noteUserId =
			currentUser?.id || orderDetails?.salesmanId || orderDetails?.salesman?.id || '';
		const nextContractNote = buildContractSentNote({
			envelopeId: nextEnvelopeId,
			signerName,
			signerEmail,
			userId: noteUserId,
		});
		const nextNotes = [...existingNotes, nextContractNote];
		const noteUpdateResult = await dispatch(
			updateOrdersNotes({
				id: orderId,
				notes: nextNotes,
			})
		);

		if (updateOrdersNotes.rejected.match(noteUpdateResult)) {
			throw new Error(
				noteUpdateResult.payload?.message ||
					noteUpdateResult.error?.message ||
					'Unable to save contract tracking.'
			);
		}

		await Promise.allSettled([
			dispatch(fetchOrderById(orderId)),
			dispatch(
				fetchOrders({
					pageSize: 1000,
					currentPage: 1,
					sortOrder: 'DESC',
					includeInvoices: true,
				})
			),
		]);
	};

	const handleStatus = async (data) => {
		await dispatch(
			updateOrderStatus({
				orderId,
				status: Number(data.status), // Convert to number
			})
		);

		await dispatch(fetchOrderById(orderId));

		await dispatch(fetchOrders());

		if (data.status === 6) {
			// Redirect to repair page
			console.log('Navigating to /repair');
			navigate('/repair');
			onClose();
		} else if (data.status === 1) {
			// Redirect to orders page
			console.log('Navigating to /orders');
			navigate('/orders');
			onClose();
		} else {
			// Close
			console.log('Closing without navigation');
			onClose();
		}
	};

	const safeJsonParse = (jsonString, defaultValue = {}) => {
        if (jsonString && typeof jsonString === 'object') return jsonString;
		if (!jsonString || typeof jsonString !== 'string') return defaultValue;
		try {
			return JSON.parse(jsonString);
		} catch (error) {
			console.error('Failed to parse JSON string:', error);
			return defaultValue;
		}
	};

	console.log('Invoice detail', activeOrder);

	// Display additional metadata information
	// Safely parse all JSON strings from the metadata
	const metadata = activeOrder?.metadata || {};
    const colorsData = safeJsonParse(metadata.colors);
	const rawPrice = safeJsonParse(metadata.price, {}); // Default to object for new structure
	// const priceArray = Array.isArray(rawPrice) ? rawPrice : []; // Legacy support if needed

	const centerSectionData = safeJsonParse(metadata.centerSection);
	const leftLeanToData = safeJsonParse(metadata.leftLeanTo);
	const rightLeanToData = safeJsonParse(metadata.rightLeanTo);
	const frontLeanToData = safeJsonParse(metadata.frontLeanTo);
	const backLeanToData = safeJsonParse(metadata.backLeanTo);
	const centerStorageData = safeJsonParse(metadata.centerStorage);
	const leftLeanStorageData = safeJsonParse(metadata.leftLeanStorage);
	const rightLeanStorageData = safeJsonParse(metadata.rightLeanStorage);
	const frontLeanStorageData = safeJsonParse(metadata.frontLeanStorage);
	const backLeanStorageData = safeJsonParse(metadata.backLeanStorage);

    // Helper to find price with fallback IDs
    const findPrice = (list, ids) => {
        if (!Array.isArray(list)) return 0;
        const idList = Array.isArray(ids) ? ids : [ids];
        const found = list.find(item => idList.includes(item.id));
        return found?.price || 0;
    };

    // Determine price lists for each section
    // Check section-specific price first, then global structured price object
    const getPriceList = (sectionData, key) => {
        // Priority 1: Price array directly on the section object
        if (Array.isArray(sectionData?.price)) return sectionData.price;
        
        // Priority 2: Price array inside metadata.price object (keyed by section)
        if (rawPrice && !Array.isArray(rawPrice) && Array.isArray(rawPrice[key])) return rawPrice[key];
        
        // Priority 3: Legacy flat array
        return Array.isArray(rawPrice) ? rawPrice : [];
    };

    const centerPrices = getPriceList(centerSectionData, 'center');
    const leftPrices = getPriceList(leftLeanToData, 'left');
    const rightPrices = getPriceList(rightLeanToData, 'right');
    const frontPrices = getPriceList(frontLeanToData, 'front');
    const backPrices = getPriceList(backLeanToData, 'back');

    let priceSummary = safeJsonParse(metadata.priceSummary);

    // Fallback: If priceSummary is missing but metadata.price has the new summary structure (subTotalPrice), use it
    if ((!priceSummary || !priceSummary.subTotal) && rawPrice && rawPrice.subTotalPrice) {
        priceSummary = {
            subTotal: rawPrice.subTotalPrice,
            estimatedTotal: rawPrice.totalPrice,
            taxAmount: rawPrice.taxAmount,
            // New structure sends percentage as 7.5, UI expects rate 0.075 for tax
            taxRate: (rawPrice.taxPercentage || 0) / 100, 
            // New structure sends percentage as 25, UI expects 25 for installationFee
            installationFee: rawPrice.installationPercentage, 
            installationFeeAmount: rawPrice.installationAmount
        };
    }

    // Get prices
	const centerStoragePrice = findPrice(centerPrices, ['csPrice', 'storagePrice']);
	const leftStoragePrice = findPrice(leftPrices, ['llsPrice', 'storagePrice']);
	const rightStoragePrice = findPrice(rightPrices, ['rlsPrice', 'storagePrice']);
	const frontStoragePrice = findPrice(frontPrices, ['flsPrice', 'storagePrice']);
	const backStoragePrice = findPrice(backPrices, ['blsPrice', 'storagePrice']);

	// --- Define variables for the display table ---

	// Base info
	const model = metadata.model;
	const installationSurface = metadata.installationSurface;
	const roofStyle = metadata.roofStyle;
	const roofPitch = metadata.roofPitch || metadata.roofPith; // Fixed typo with fallback

	// Center Section details
	const csSize = centerSectionData.size || {};
	const csSides = centerSectionData.sidesAndEnds || {};
	const csItems = centerSectionData.items || {};

	// Right Lean-To details
	const rightLeanToActive = rightLeanToData.leanTo;
	const rltSize = rightLeanToData.size || {};
	const rltSides = rightLeanToData.sidesAndEnds || {};

	// Left Lean-To details
	const leftLeanToActive = leftLeanToData.leanTo;
	const lltSize = leftLeanToData.size || {};
	const lltSides = leftLeanToData.sidesAndEnds || {};

	// Front Lean-To details
	const frontLeanToActive = frontLeanToData.leanTo;
	const fltSize = frontLeanToData.size || {};
	const fltSides = frontLeanToData.sidesAndEnds || {};

	// Back Lean-To details
	const backLeanToActive = backLeanToData.leanTo;
	const bltSize = backLeanToData.size || {};
	const bltSides = backLeanToData.sidesAndEnds || {};



	const basePrice = findPrice(centerPrices, ['basePrice', 'mbPrice']);
	const centerLegPrice = findPrice(centerPrices, ['legPrice', 'clPrice']);
	const roofStylePrice = findPrice(centerPrices, ['roofPrice', 'rsPrice']);
	const roofPitchPrice = findPrice(centerPrices, ['rpPrice']);
	const rightLeanPrice = findPrice(rightPrices, ['basePrice', 'rlPrice']);
	const rightLegPrice = findPrice(rightPrices, ['legPrice', 'rLegPrice']);
	const leftLeanPrice = findPrice(leftPrices, ['basePrice', 'llPrice']);
	const leftLegPrice = findPrice(leftPrices, ['legPrice', 'lLegPrice']);
	const frontLeanPrice = findPrice(frontPrices, ['basePrice', 'flPrice']);
	const frontLegPrice = findPrice(frontPrices, ['legPrice', 'fLegPrice']);
	const backLeanPrice = findPrice(backPrices, ['basePrice', 'blPrice']);
	const backLegPrice = findPrice(backPrices, ['legPrice', 'bLegPrice']);

	const discount = activeOrder?.discountAmount || 0;
	// const orderMetadata = orderDetails?.metadata?.size;
	// let orderMetadataParsed;
	// try {
	// 	orderMetadataParsed = JSON?.parse(orderMetadata);
	// } catch (error) {
	// 	console.error('Failed to parse orderMetadata:', error);
	// 	orderMetadataParsed = null; // or set a default value
	// }

	const orderMetadataColors = activeOrder?.metadata?.colors;
	const orderMetadataRoofStyle = activeOrder?.metadata?.roofStyle;
	const orderMetadataRoofPitch = activeOrder?.metadata?.roofPitch;
	const orderMetadataSidesAndEnds = activeOrder?.metadata?.sidesAndEnds;

	// const orderMetadataPrice = orderDetails?.metadata?.price;
	// let orderMetadataPriceParsed;
	// try {
	// 	orderMetadataPriceParsed = JSON.parse(orderMetadataPrice);
	// } catch (error) {
	// 	console.error('Failed to parse orderMetadataPrice:', error);
	// 	orderMetadataPriceParsed = null; // or set a default value
	// }

	// Calculate totals based on extracted prices
	// Calculate totals based on extracted prices
	const subTotal = priceSummary?.subTotal || (
		basePrice +
		roofStylePrice +
		roofPitchPrice +
		centerLegPrice -
		discount
    );
	const taxes = priceSummary?.taxAmount || (subTotal * (activeOrder?.taxRate || 0));
	const total = priceSummary?.estimatedTotal || (subTotal + taxes);

	// const subTotal = totalLegPrice + totalEstimate - totalDiscount;
	// const taxes = subTotal * 0.0775;
	// const total = subTotal + subTotal * 0.0775;

	// Button to send to 3d Design design.bncaccess.com

		const sendToDesign = () => {
			if (!orderDetails?.id) return;
		        // Construct clean payload with parsed structures and explicit price lists
		        const designPayload = {
		            ...orderDetails,
		            metadata: {
	                ...(orderDetails?.metadata || {}),
	                // Ensure sections are objects, not JSON strings
	                centerSection: centerSectionData,
	                leftLeanTo: leftLeanToData,
	                rightLeanTo: rightLeanToData,
                frontLeanTo: frontLeanToData,
                backLeanTo: backLeanToData,
                
                // Storage
                centerStorage: centerStorageData,
                leftLeanStorage: leftLeanStorageData,
                rightLeanStorage: rightLeanStorageData,
                frontLeanStorage: frontLeanStorageData,
                backLeanStorage: backLeanStorageData,
                
                // Colors
                colors: colorsData,
                
                // Pricing (Structured)
                price: {
                    center: centerPrices,
                    left: leftPrices,
                    right: rightPrices,
                    front: frontPrices,
                    back: backPrices
                }
            }
        };

        console.log('Payload for 3D Design:', designPayload);

		const designUrl = `https://design.bncaccess.com/${orderDetails.id}`;
		window.open(designUrl, '_blank');
	};

	// handle installer send orderId and installerId

	const handleInstaller = async (event) => {
		if (!orderId) return;
		const selectedInstallerId = event.target.value;
		setValue('installerId', selectedInstallerId);
		const payload = {
			orderId,
			installerId: selectedInstallerId, // Corrected this line
		};

		await dispatch(updateInstallerId(payload));
		await dispatch(fetchOrderById(orderId));
	};

	const prepareContractPreview = async () => {
		if (!signerEmail || !signerName) {
			alert('Client email and name are required to preview the contract.');
			return null;
		}

		if (!orderDetails) {
			alert('Order details are still loading. Please try again.');
			return null;
		}

		setIsPreparingContract(true);
		try {
			const pdfDataUri = await generateContractPDF(orderDetails);
			const base64Doc = pdfDataUri.split(',')[1];
			const reference = orderDetails?.reference || orderDetails?.id || 'contract';
			const nextPreview = {
				base64Doc,
				name: `Contract-${reference}.pdf`,
				type: 'application/pdf',
				url: pdfDataUri,
			};
			setContractPreview(nextPreview);
			return nextPreview;
		} catch (error) {
			console.error('Error preparing contract preview:', error);
			alert(`Failed to prepare contract preview: ${error.message}`);
			return null;
		} finally {
			setIsPreparingContract(false);
		}
	};

	const handleSendContract = async () => {
		if (!signerEmail || !signerName) {
			alert('Client email and name are required to send contract.');
			return;
		}

		setIsSending(true);
		try {
			const previewToSend = contractPreview?.base64Doc
				? contractPreview
				: await prepareContractPreview();

			if (!previewToSend?.base64Doc) {
				return;
			}

			const response = await sendContractApi({
				email: signerEmail,
				name: signerName,
				document: previewToSend.base64Doc,
			});

			if (response.data && response.data.envelopeId) {
				const nextEnvelopeId = response.data.envelopeId;
				setEnvelopeId(nextEnvelopeId);
				setIsContractViewerOpen(false);

				let trackingWarning = '';
				try {
					await persistContractSentEvent(nextEnvelopeId);
				} catch (trackingError) {
					console.error('Contract sent, but tracking note failed to save:', trackingError);
					trackingWarning = ` Contract was sent, but the sent-contracts list could not be updated: ${trackingError.message}`;
				}

				alert(
					trackingWarning
						? `Envelope sent! ID: ${nextEnvelopeId}.${trackingWarning}`
						: `Envelope sent! ID: ${nextEnvelopeId}`
				);
			}
		} catch (error) {
			console.error('Error sending contract:', error);
			const errorMsg = error.response 
				? `Status: ${error.response.status}, Data: ${JSON.stringify(error.response.data)}`
				: error.message;
			alert(`Failed to send contract: ${errorMsg}`);
		} finally {
			setIsSending(false);
		}
	};

	const handlePreviewContract = async () => {
		const preview = await prepareContractPreview();
		if (preview?.base64Doc) {
			setIsContractViewerOpen(true);
		}
	};



	// Loading spinner is shown above (before data access) only when no data is available.
	// If activeOrder is available, we render with partial data and refresh in the background.

	return (
		<div className="order-details-panel">
            <FileViewerModal 
                isOpen={viewerOpen}
                onClose={() => {
                    setViewerOpen(false);
                    setSelectedFile(null);
                }}
	                fileUrl={selectedFile?.url}
	                fileName={selectedFile?.name}
	                fileType={selectedFile?.type}
	            />
			<FileViewerModal
				isOpen={isContractViewerOpen}
				onClose={() => setIsContractViewerOpen(false)}
				fileUrl={contractPreview?.url}
				fileName={contractPreview?.name || 'Contract Preview'}
				fileType={contractPreview?.type}
				footerContent={
					<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
						<p className="text-sm text-slate-600">
							Review the contract before sending it to the client.
						</p>
						<div className="flex items-center justify-end gap-3">
							<button
								type="button"
								onClick={() => setIsContractViewerOpen(false)}
								className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
							>
								Close
							</button>
							<button
								type="button"
								onClick={handleSendContract}
								disabled={isSending || isPreparingContract || !contractPreview?.base64Doc}
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{isSending ? 'Sending Contract...' : 'Send Contract'}
							</button>
						</div>
					</div>
				}
			/>
			<aside className="details-panel" onClick={(e) => e.stopPropagation()}>
				<div className="order-number">
					<div className="block-left">
						<h3 className="order-number-title">Order {order.reference}</h3>
						{orderDetails?.updatedAt && (
							<p className=" text-sm">
								<strong>Last updated</strong>:{' '}
								{new Date(orderDetails.updatedAt).toLocaleString('en-US', {
									month: '2-digit',
									day: '2-digit',
									year: '2-digit',
									hour: '2-digit',
									minute: '2-digit',
								})}
							</p>
						)}
					</div>
					<div className="block-right">
						<button onClick={onClose} className="close-btn">
							<X size={24} />
						</button>
					</div>
				</div>

					{!orderLoaded ? (
					<div className="p-8">
						{detailsStatus === 'failed' && (
							<div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
								<p className="font-semibold">Could not load order details.</p>
								<p className="text-sm">{orderError?.message || 'Please try again.'}</p>
							</div>
						)}
						<div className="flex flex-col items-center justify-center gap-4 py-12">
							<BncLogoSpinner size={80} />
							<p className="text-slate-500 text-sm">Loading order details...</p>
						</div>
					</div>
				) : (
					<>
				<div className="details-client-quick-info">
					<h3>
						{orderDetails?.client?.firstName} {orderDetails?.client?.lastName}{' '}
					</h3>
					<p> {orderDetails?.client?.companyName} </p>
					{/* Icons with links for email, phone and edit */}
					<div className="details-client-icons">
						{orderDetails?.client?.email && (
							<a
								href={`mailto:${orderDetails?.client?.email}`}
								title="Send Email"
							>
								<Mail size={24} className="icon-item" />
							</a>
						)}
						{orderDetails?.client?.phoneNumber && (
							<a
								href={`tel:${orderDetails?.client?.phoneNumber}`}
								title="Call Phone"
							>
								<PhoneOutgoing size={24} className="icon-item" />
							</a>
						)}
						{/* create button to add notes a button with a click open toggleSection('notes')} and it's ready to add a new note */}
						<button
							onClick={() => toggleSection('notes')}
							title="Add Note"
							className="relative"
						>
							<FileText size={24} className="icon-item" />
							{orderDetails?.notes && orderDetails.notes.length > 0 && (
								<span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
									{orderDetails.notes.length}
								</span>
							)}
						</button>
					</div>
				</div>

				<div className="progress-guide">
					<div className="progress-guide-block w-full">
						<h2 className="progress-guide-title">
							<ProgressBar currentStatus={orderDetails.status} />
						</h2>
					</div>
				</div>
				<OrderNotesSection 
					sections={sections}
					toggleSection={toggleSection}
					formRef={formRef}
					onSubmitFile={onSubmitFile}
					handleSubmit={handleSubmit}
					register={register}
					errors={errors}
					fileInputs={fileInputs}
					fileUploadHandler={fileUploadHandler}
					removeFileInput={removeFileInput}
					addFileInput={addFileInput}
					Msg={Msg}
					progress={progress}
					orderDetails={orderDetails}
					getUserName={getUserName}
                    onViewAttachment={(file) => {
                        setSelectedFile(file);
                        setViewerOpen(true);
                    }}
				/>

				<OrderSalesmanSection 
					sections={sections}
					toggleSection={toggleSection}
					orderDetails={orderDetails}
				/>

				<OrderClientInfoSection 
					sections={sections}
					toggleSection={toggleSection}
					orderDetails={orderDetails}
				/>

				<OrderPricingSummary 
					sections={sections}
					toggleSection={toggleSection}
					model={model}
					installationSurface={installationSurface}
					roofStyle={roofStyle}
					roofPitch={roofPitch}
					csSize={csSize}
					basePrice={basePrice}
					centerLegPrice={centerLegPrice}
					csSides={csSides}
                    csItems={csItems}
					rightLeanToActive={rightLeanToActive}
					rltSize={rltSize}
                    rltItems={rightLeanToData.items || []}
					rightLeanPrice={rightLeanPrice}
					rightLegPrice={rightLegPrice}
					leftLeanToActive={leftLeanToActive}
					lltSize={lltSize}
                    lltItems={leftLeanToData.items || []}
					leftLeanPrice={leftLeanPrice}
					leftLegPrice={leftLegPrice}
					frontLeanToActive={frontLeanToActive}
					fltSize={fltSize}
					fltItems={frontLeanToData.items || []}
					frontLeanPrice={frontLeanPrice}
					frontLegPrice={frontLegPrice}
					backLeanToActive={backLeanToActive}
					bltSize={bltSize}
					bltItems={backLeanToData.items || []}
					backLeanPrice={backLeanPrice}
					backLegPrice={backLegPrice}
					colorsData={colorsData}
                    // Storage
                    centerStorageData={centerStorageData}
                    leftLeanStorageData={leftLeanStorageData}
                    rightLeanStorageData={rightLeanStorageData}
                    frontLeanStorageData={frontLeanStorageData}
                    backLeanStorageData={backLeanStorageData}
                    centerStoragePrice={centerStoragePrice}
                    leftStoragePrice={leftStoragePrice}
                    rightStoragePrice={rightStoragePrice}
                    frontStoragePrice={frontStoragePrice}
                    backStoragePrice={backStoragePrice}
                    
                    // Price Arrays (for lookups)
                    centerPrices={centerPrices}
                    leftPrices={leftPrices}
                    rightPrices={rightPrices}
                    frontPrices={frontPrices}
                    backPrices={backPrices}
                    
                    // Price Summary
                    priceSummary={priceSummary}
				/>
				<OrderInstallerSection 
					sections={sections}
					toggleSection={toggleSection}
					orderDetails={orderDetails}
					currentUser={currentUser}
					register={register}
					handleInstaller={handleInstaller}
					installers={installers}
				/>

				<OrderPermitsSection 
					orderDetails={orderDetails}
					sections={sections}
					toggleSection={toggleSection}
					handleFileUpload={handleFileUpload}
					permitMsg={permitMsg}
					handlePermitSubmit={handlePermitSubmit}
				/>

				<OrderPaymentHistorySection 
					sections={sections}
					toggleSection={toggleSection}
					orderDetails={orderDetails}
				/>

				<OrderStatusSection 
					sections={sections}
					toggleSection={toggleSection}
					handleSubmit={handleSubmit}
					handleStatus={handleStatus}
					register={register}
					sendToDesign={sendToDesign}
					envelopeId={envelopeId}
                    watch={watch}
                    setValue={setValue}
                    onPreviewContract={handlePreviewContract}
                    isSending={isSending}
                    isPreparingContract={isPreparingContract}
				/>
					</>
				)}
			</aside>
		</div>
	);
}
